import { useState, useEffect } from 'react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getSites, createSite, updateSite, deleteSite } from '../services/api';
import { canDelete } from '../utils/auth';

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    gst: '',
    contactPerson: ''
  });
  const [newCustomerSites, setNewCustomerSites] = useState([]);
  const [editingCustomerSites, setEditingCustomerSites] = useState([]);
  const [siteFormData, setSiteFormData] = useState({
    customerId: '',
    siteName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    contactPerson: '',
    phone: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (viewingCustomer) {
      loadSites(viewingCustomer.id);
    }
  }, [viewingCustomer]);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      alert('Error loading customers: ' + error.message);
    }
  };

  const loadSites = async (customerId) => {
    try {
      const data = await getSites(customerId);
      setSites(data);
    } catch (error) {
      alert('Error loading sites: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, formData);

        // Update existing sites
        for (const site of editingCustomerSites) {
          await updateSite(site.id, {
            siteName: site.siteName,
            address: site.address,
            city: site.city || '',
            state: site.state || '',
            pincode: site.pincode || '',
            contactPerson: site.contactPerson || '',
            phone: site.phone || ''
          });
        }

        // Create new sites added during editing
        for (const site of newCustomerSites) {
          await createSite({
            customerId: editingCustomer.id,
            siteName: site.siteName,
            address: site.address,
            city: site.city || '',
            state: site.state || '',
            pincode: site.pincode || '',
            contactPerson: site.contactPerson || '',
            phone: site.phone || ''
          });
        }
      } else {
        const newCustomer = await createCustomer(formData);

        // Create associated sites if any were added
        if (newCustomerSites.length > 0) {
          for (const site of newCustomerSites) {
            await createSite({
              customerId: newCustomer.id,
              siteName: site.siteName,
              address: site.address,
              city: site.city || '',
              state: site.state || '',
              pincode: site.pincode || '',
              contactPerson: site.contactPerson || '',
              phone: site.phone || ''
            });
          }
        }
      }
      setShowCustomerForm(false);
      setEditingCustomer(null);
      setFormData({ name: '', email: '', phone: '', address: '', gst: '', contactPerson: '' });
      setNewCustomerSites([]);
      setEditingCustomerSites([]);
      loadCustomers();
    } catch (error) {
      alert('Error saving customer: ' + error.message);
    }
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    try {
      await createSite(siteFormData);
      setShowSiteForm(false);
      setSiteFormData({
        customerId: '',
        siteName: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        contactPerson: '',
        phone: ''
      });
      if (viewingCustomer) {
        loadSites(viewingCustomer.id);
      }
    } catch (error) {
      alert('Error saving site: ' + error.message);
    }
  };

  const handleViewDetails = (customer) => {
    setViewingCustomer(customer);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(id);
        loadCustomers();
        if (viewingCustomer && viewingCustomer.id === id) {
          setShowViewModal(false);
          setViewingCustomer(null);
          setSites([]);
        }
      } catch (error) {
        alert('Error deleting customer: ' + error.message);
      }
    }
  };

  const handleEdit = async (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      gst: customer.gst || '',
      contactPerson: customer.contactPerson || ''
    });

    // Load existing sites for this customer
    try {
      const customerSites = await getSites(customer.id);
      // Map backend snake_case to camelCase for form fields
      const mappedSites = customerSites.map(site => ({
        ...site,
        siteName: site.site_name || site.siteName,
        contactPerson: site.contact_person || site.contactPerson
      }));
      setEditingCustomerSites(mappedSites);
    } catch (error) {
      console.error('Error loading sites:', error);
      setEditingCustomerSites([]);
    }

    setShowCustomerForm(true);
  };

  const handleAddSite = (customer) => {
    setSiteFormData({
      ...siteFormData,
      customerId: customer.id
    });
    setShowSiteForm(true);
  };

  const handleAddSiteToNewCustomer = () => {
    setNewCustomerSites([
      ...newCustomerSites,
      {
        siteName: '',
        address: ''
      }
    ]);
  };

  const handleRemoveSiteFromNewCustomer = (index) => {
    setNewCustomerSites(newCustomerSites.filter((_, i) => i !== index));
  };

  const handleUpdateNewCustomerSite = (index, field, value) => {
    const updatedSites = [...newCustomerSites];
    updatedSites[index][field] = value;
    setNewCustomerSites(updatedSites);
  };

  const handleUpdateEditingCustomerSite = (index, field, value) => {
    const updatedSites = [...editingCustomerSites];
    updatedSites[index][field] = value;
    setEditingCustomerSites(updatedSites);
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const getFilteredCustomers = () => {
    if (!searchTerm) {
      return customers;
    }

    return customers.filter(customer => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = (customer.phone || '').toLowerCase();
      const contactPerson = (customer.contactPerson || '').toLowerCase();
      const gst = (customer.gst || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      return name.includes(searchLower) ||
             email.includes(searchLower) ||
             phone.includes(searchLower) ||
             contactPerson.includes(searchLower) ||
             gst.includes(searchLower);
    });
  };

  const filteredCustomers = getFilteredCustomers();

  const buttonStyle = {
    padding: '10px 20px',
    background: '#C8342B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    marginRight: '10px',
    fontWeight: '500'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#3D1F1F' }}>Customer Master</h1>
        <button
          onClick={() => {
            setShowCustomerForm(true);
            setEditingCustomer(null);
            setFormData({ name: '', email: '', phone: '', address: '', gst: '', contactPerson: '' });
            setNewCustomerSites([]);
          }}
          style={buttonStyle}
        >
          Add Customer
        </button>
      </div>

      {/* Search Bar */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <label style={{
          display: 'block',
          marginBottom: '5px',
          fontSize: '12px',
          fontWeight: '600',
          color: '#7f8c8d'
        }}>
          Search
        </label>
        <input
          type="text"
          placeholder="Search by name, email, phone, contact person, or GST..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Customer Form Modal */}
      {showCustomerForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '650px',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
            <h2 style={{ marginTop: 0 }}>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h2>
            <form onSubmit={handleSubmit} style={{ width: '100%', boxSizing: 'border-box' }}>
              <input
                style={inputStyle}
                type="text"
                placeholder="Customer Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <input
                style={inputStyle}
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <input
                style={inputStyle}
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <textarea
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                placeholder="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="GST Number"
                value={formData.gst}
                onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="Contact Person"
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              />

              {/* Site Addresses Section */}
              {(
                <div style={{
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '2px solid #ecf0f1',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    gap: '10px',
                    flexWrap: 'wrap'
                  }}>
                    <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>Site Addresses</h3>
                    <button
                      type="button"
                      onClick={handleAddSiteToNewCustomer}
                      style={{
                        ...buttonStyle,
                        padding: '6px 12px',
                        fontSize: '13px',
                        background: '#27ae60',
                        marginRight: 0,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + Add Site Address
                    </button>
                  </div>

                  {editingCustomer && editingCustomerSites.length === 0 && newCustomerSites.length === 0 ? (
                    <p style={{ color: '#7f8c8d', fontSize: '14px', fontStyle: 'italic' }}>
                      No site addresses added. Click "Add Site Address" to add one.
                    </p>
                  ) : !editingCustomer && newCustomerSites.length === 0 ? (
                    <p style={{ color: '#7f8c8d', fontSize: '14px', fontStyle: 'italic' }}>
                      No site addresses added. Click "Add Site Address" to add one.
                    </p>
                  ) : (
                    <div style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      paddingRight: '5px'
                    }}>
                      {/* Show existing sites when editing */}
                      {editingCustomer && editingCustomerSites.map((site, index) => (
                        <div
                          key={`existing-${index}`}
                          style={{
                            padding: '15px',
                            marginBottom: '15px',
                            border: '1px solid #3498db',
                            borderRadius: '4px',
                            background: '#ebf5fb',
                            position: 'relative',
                            boxSizing: 'border-box',
                            width: '100%'
                          }}
                        >
                          <div style={{ marginBottom: '4px', fontSize: '11px', color: '#3498db', fontWeight: 'bold' }}>
                            EXISTING SITE
                          </div>
                          <div style={{ marginBottom: '8px', width: '100%' }}>
                            <label style={{ fontSize: '12px', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>
                              Site Name *
                            </label>
                            <input
                              style={{
                                ...inputStyle,
                                marginBottom: 0,
                                boxSizing: 'border-box',
                                width: '100%'
                              }}
                              type="text"
                              placeholder="e.g., Main Office, Warehouse 1"
                              value={site.siteName}
                              onChange={(e) => handleUpdateEditingCustomerSite(index, 'siteName', e.target.value)}
                              required
                            />
                          </div>
                          <div style={{ width: '100%' }}>
                            <label style={{ fontSize: '12px', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>
                              Site Address *
                            </label>
                            <textarea
                              style={{
                                ...inputStyle,
                                minHeight: '60px',
                                marginBottom: 0,
                                boxSizing: 'border-box',
                                width: '100%',
                                resize: 'vertical'
                              }}
                              placeholder="Enter complete address"
                              value={site.address}
                              onChange={(e) => handleUpdateEditingCustomerSite(index, 'address', e.target.value)}
                              required
                            />
                          </div>
                        </div>
                      ))}

                      {/* Show new sites being added */}
                      {newCustomerSites.map((site, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '15px',
                            paddingRight: '45px',
                            marginBottom: '15px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#f8f9fa',
                            position: 'relative',
                            boxSizing: 'border-box',
                            width: '100%'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleRemoveSiteFromNewCustomer(index)}
                            style={{
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            ×
                          </button>
                          <div style={{ marginBottom: '8px', width: '100%' }}>
                            <label style={{ fontSize: '12px', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>
                              Site Name *
                            </label>
                            <input
                              style={{
                                ...inputStyle,
                                marginBottom: 0,
                                boxSizing: 'border-box',
                                width: '100%'
                              }}
                              type="text"
                              placeholder="e.g., Main Office, Warehouse 1"
                              value={site.siteName}
                              onChange={(e) => handleUpdateNewCustomerSite(index, 'siteName', e.target.value)}
                              required
                            />
                          </div>
                          <div style={{ width: '100%' }}>
                            <label style={{ fontSize: '12px', color: '#7f8c8d', display: 'block', marginBottom: '4px' }}>
                              Site Address *
                            </label>
                            <textarea
                              style={{
                                ...inputStyle,
                                minHeight: '60px',
                                marginBottom: 0,
                                boxSizing: 'border-box',
                                width: '100%',
                                resize: 'vertical'
                              }}
                              placeholder="Enter complete address"
                              value={site.address}
                              onChange={(e) => handleUpdateNewCustomerSite(index, 'address', e.target.value)}
                              required
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '20px',
                paddingTop: '15px',
                borderTop: '1px solid #ecf0f1',
                flexWrap: 'wrap'
              }}>
                <button type="submit" style={buttonStyle}>
                  {editingCustomer ? 'Update' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomerForm(false);
                    setEditingCustomer(null);
                    setNewCustomerSites([]);
                    setEditingCustomerSites([]);
                  }}
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Site Form Modal */}
      {showSiteForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '550px',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
            <h2 style={{ marginTop: 0 }}>Add New Site</h2>
            <form onSubmit={handleSiteSubmit} style={{ width: '100%', boxSizing: 'border-box' }}>
              <input
                style={inputStyle}
                type="text"
                placeholder="Site Name *"
                value={siteFormData.siteName}
                onChange={(e) => setSiteFormData({ ...siteFormData, siteName: e.target.value })}
                required
              />
              <textarea
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                placeholder="Address"
                value={siteFormData.address}
                onChange={(e) => setSiteFormData({ ...siteFormData, address: e.target.value })}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="City"
                value={siteFormData.city}
                onChange={(e) => setSiteFormData({ ...siteFormData, city: e.target.value })}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="State"
                value={siteFormData.state}
                onChange={(e) => setSiteFormData({ ...siteFormData, state: e.target.value })}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="Pincode"
                value={siteFormData.pincode}
                onChange={(e) => setSiteFormData({ ...siteFormData, pincode: e.target.value })}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="Contact Person"
                value={siteFormData.contactPerson}
                onChange={(e) => setSiteFormData({ ...siteFormData, contactPerson: e.target.value })}
              />
              <input
                style={inputStyle}
                type="tel"
                placeholder="Phone"
                value={siteFormData.phone}
                onChange={(e) => setSiteFormData({ ...siteFormData, phone: e.target.value })}
              />
              <div style={{
                display: 'flex',
                gap: '10px',
                marginTop: '5px',
                paddingTop: '15px',
                borderTop: '1px solid #ecf0f1',
                flexWrap: 'wrap'
              }}>
                <button type="submit" style={buttonStyle}>
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowSiteForm(false)}
                  style={{ ...buttonStyle, background: '#95a5a6' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Customer Details Modal */}
      {showViewModal && viewingCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowViewModal(false)}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#3D1F1F' }}>{viewingCustomer.name}</h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#7f8c8d',
                  padding: '0',
                  width: '30px',
                  height: '30px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3D1F1F'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#7f8c8d'}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '25px', paddingBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
              <h3 style={{ fontSize: '16px', color: '#3D1F1F', marginBottom: '12px' }}>Customer Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {viewingCustomer.email && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Email</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                      {viewingCustomer.email}
                    </div>
                  </div>
                )}
                {viewingCustomer.phone && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Phone</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                      {viewingCustomer.phone}
                    </div>
                  </div>
                )}
                {viewingCustomer.contactPerson && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Contact Person</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                      {viewingCustomer.contactPerson}
                    </div>
                  </div>
                )}
                {viewingCustomer.gst && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>GST Number</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                      {viewingCustomer.gst}
                    </div>
                  </div>
                )}
              </div>
              {viewingCustomer.address && (
                <div style={{ marginTop: '15px' }}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Address</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                    {viewingCustomer.address}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '16px', color: '#3D1F1F', margin: 0 }}>Sites</h3>
                <button
                  onClick={() => handleAddSite(viewingCustomer)}
                  style={{ ...buttonStyle, padding: '6px 12px', fontSize: '13px', background: '#27ae60', margin: 0 }}
                >
                  + Add Site
                </button>
              </div>
              {sites.length === 0 ? (
                <p style={{ color: '#7f8c8d', fontSize: '14px' }}>No sites found for this customer.</p>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {sites.map(site => (
                    <div
                      key={site.id}
                      style={{
                        padding: '15px',
                        marginBottom: '12px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        background: '#f8f9fa'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '15px' }}>{site.site_name || site.siteName || 'Unnamed Site'}</h4>
                        {canDelete() && (
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete site "${site.site_name || site.siteName}"?`)) {
                                try {
                                  await deleteSite(site.id);
                                  loadSites(viewingCustomer.id);
                                } catch (error) {
                                  alert('Error deleting site: ' + error.message);
                                }
                              }
                            }}
                            style={{
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#c0392b'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#e74c3c'}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      {site.address && (
                        <p style={{ margin: '5px 0', fontSize: '13px', color: '#7f8c8d' }}>
                          {site.address}
                        </p>
                      )}
                      {(site.city || site.state || site.pincode) && (
                        <p style={{ margin: '5px 0', fontSize: '13px', color: '#7f8c8d' }}>
                          {site.city && site.state && `${site.city}, ${site.state}`}
                          {site.pincode && ` - ${site.pincode}`}
                        </p>
                      )}
                      {(site.contact_person || site.contactPerson) && (
                        <p style={{ margin: '5px 0', fontSize: '13px', color: '#7f8c8d' }}>
                          Contact: {site.contact_person || site.contactPerson}
                          {site.phone && ` (${site.phone})`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer List */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {customers.length === 0 ? (
          <p style={{ color: '#7f8c8d' }}>No customers found. Add your first customer!</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
            {filteredCustomers.map(customer => (
              <div
                key={customer.id}
                onClick={() => handleViewDetails(customer)}
                style={{
                  padding: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>{customer.name}</h3>
                {customer.phone && (
                  <p style={{ margin: '3px 0', fontSize: '14px', color: '#7f8c8d' }}>
                    Phone: {customer.phone}
                  </p>
                )}
                {customer.email && (
                  <p style={{ margin: '3px 0', fontSize: '14px', color: '#7f8c8d' }}>
                    Email: {customer.email}
                  </p>
                )}
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(customer);
                    }}
                    style={{ ...buttonStyle, padding: '6px 12px', fontSize: '12px', margin: 0 }}
                  >
                    Edit
                  </button>
                  {canDelete() && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(customer.id);
                      }}
                      style={{ ...buttonStyle, padding: '6px 12px', fontSize: '12px', background: '#e74c3c', margin: 0 }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Customers;
